// Timeline de rota da carona.
//
// Serve dois consumidores: o card de resultado da busca (2 pontos, origem e
// destino pesquisados) e o cabeçalho da página de carona (N pontos, rota
// completa com as cidades de passagem). A parte de apresentação vive em
// `components/RouteTimeline.tsx`; aqui fica só o cálculo, para que os grupos
// (antes/dentro/depois do trecho), a decisão de expandir ou não e o tempo de
// cada ponto fiquem testáveis sem jsdom.
//
// Coordenadas são resolvidas aqui mesmo: `getRoutePoints` já passa por
// `locationPoint`, então um documento cru do Mongo (GeoJSON, sem `lat`/`lng`)
// sai com as coordenadas no lugar. O componente nunca toca em coordenada.
//
// Em modo contínuo (turbo) o passageiro embarca em qualquer ponto da rota, não
// numa parada. Se esse km cair entre duas paradas, o trilho ganha uma linha
// sintética de embarque/desembarque com o nome que a busca mostrou.

import { getRoutePoints, cumulativeKmAtRoutePoints, type RoutePoint } from "@/lib/segment-utils"
import { computePointTimes, formatTime, rideStartDate } from "@/lib/segment-times"
import { locationPoint, type RideLocationLike } from "@/lib/ride-normalize"
import { splitPlaceName } from "@/lib/place-name"
import { ceilMinutesTo5, ceilDurationTo5 } from "@/lib/utils"

export type RouteTimelineKind = "origin" | "stop" | "destination" | "board" | "alight"

/**
 * O ponto está antes do embarque, dentro do trecho, depois do desembarque ou
 * é uma parada de passagem no meio da rota inteira (trecho cobre tudo).
 */
export type RouteTimelineGroup = "before" | "in" | "after" | "middle"

export interface RouteTimelinePoint {
  /** Índice no espaço de `getRoutePoints`; negativo nas linhas sintéticas. */
  index: number
  /** Nome de exibição: cidade quando é parada de cidade, senão o nome curto. */
  name: string
  /** Região/UF abaixo do nome (2ª linha). `null`/ausente = uma linha só. */
  secondary?: string | null
  /** Horário estimado (HH:MM, arredondado para 5 min). `""` quando desconhecido. */
  time: string
  kind: RouteTimelineKind
  group: RouteTimelineGroup
  /** Quilômetros percorridos do início da rota até este ponto. */
  alongKm: number
  /** Linha de embarque/desembarque que não existe como parada da rota. */
  synthetic?: boolean
  /** Reservas que embarcam neste ponto. */
  boarders?: Array<{ name: string; avatar: string | null }>
  /** Reservas que desembarcam neste ponto. */
  alighters?: Array<{ name: string; avatar: string | null }>
}

export interface RouteTimelineData {
  points: RouteTimelinePoint[]
  /** Do primeiro ao último ponto da rota, em minutos. `null` sem tempo confiável. */
  durationMinutes: number | null
  /** Do embarque ao desembarque, em minutos. `null` quando não dá para saber. */
  trechoDurationMinutes: number | null
}

export interface BuildRouteTimelineOptions {
  boardIndex?: number
  alightIndex?: number
  /**
   * Em modo contínuo (turbo) `boardStopIndex`/`alightStopIndex` não existem — os
   * limites vêm em km (`boardKm`/`alightKm`). Sem km válido não há como apontar
   * o trecho, então a rota inteira entra como "dentro" em vez de apagar alguém.
   */
  continuousMode?: boolean
  /** Along em km do embarque/desembarque — só no modo contínuo. */
  boardKm?: number
  alightKm?: number
  /** Rótulo do embarque/desembarque, para a linha sintética. */
  boardName?: string | null
  alightName?: string | null
  /**
   * Cidade IBGE ("Ubiratã, PR") do embarque/desembarque consultado. Com ela a
   * linha sintética mostra a cidade em destaque e o endereço completo abaixo
   * (mesmo formato das paradas), em vez do primeiro segmento cru do nome.
   */
  boardCityName?: string | null
  alightCityName?: string | null
  /** Horário (HH:MM) do embarque/desembarque, como a busca mostrou. */
  boardTime?: string | null
  alightTime?: string | null
  /**
   * Vindo da busca: o trilho é o trecho consultado, então tudo que está
   * estritamente entre o embarque e o desembarque (paradas de passagem e linhas
   * de reserva de outros passageiros) fica atrás de um "… ver N paradas". Sem
   * isto, o colapso só acontece quando o trecho cobre a rota inteira ou quando
   * não sobrou nada fora dele — o caso que deixava a parada do meio solta na
   * tela quando o destino pesquisado não era o destino final da carona.
   */
  collapseIntermediate?: boolean
  /**
   * Reservas ativas para mostrar embarques/desembarques de todos os passageiros.
   * A âncora de cada ponta é resolvida em `resolveReservationEnd`: km (turbo) →
   * índice da parada → nome, nessa ordem. O km vence porque é o dado gravado
   * pela busca; o índice pode estar desatualizado (parada renumerada) e o nome
   * falha quando a parada tem nome próprio (ex.: "Havan" ≠ "Cascavel").
   */
  reservations?: Array<{
    origin: string | null
    destination: string | null
    name: string
    avatar: string | null
    boardStopIndex?: number | null
    alightStopIndex?: number | null
    boardKm?: number | null
    alightKm?: number | null
  }>
}

/** Índices negativos para as linhas sintéticas não colidirem com a rota. */
const SYNTHETIC_BOARD_INDEX = -1
const SYNTHETIC_ALIGHT_INDEX = -2

/**
 * Gera índice determinístico para pontos sintéticos de embarque/desembarque.
 * Usa hash simples do nome do local para garantir estabilidade entre renders.
 */
function syntheticIndex(key: string, isBoard: boolean): number {
  let hash = 0
  for (let i = 0; i < key.length; i++) {
    hash = ((hash << 5) - hash) + key.charCodeAt(i)
    hash |= 0
  }
  return isBoard ? -1000 - Math.abs(hash) : -2000 - Math.abs(hash)
}

/**
 * Um ponto da rota a menos de 50 m do embarque/desembarque É o embarque: a
 * diferença existe só porque o km da busca e o km reprojetado são calculados
 * em lugares diferentes. Mesmo epsilon de `KM_OVERLAP_EPSILON_KM`.
 */
const COINCIDENT_EPSILON_KM = 0.05

/**
 * A listagem com a rota e as cidades de passagem aparece quando a rota do
 * passageiro não é a rota inteira (origem/destino deslocados do traçado) ou
 * quando a carona passa por cidades. Só nos dois casos negativos é que vale a
 * visualização curta de hoje (origem → destino direto).
 */
export function shouldExpandRouteTimeline(input: {
  originDisplacement?: boolean
  destDisplacement?: boolean
  cityStopCount?: number
}): boolean {
  return (
    !!input.originDisplacement ||
    !!input.destDisplacement ||
    (input.cityStopCount ?? 0) > 0
  )
}

/** `130` → `"2h 10min"`, `45` → `"45min"`. Arredonda para cima em 5 min. */
export function formatDurationMinutes(minutes: number): string {
  const total = ceilMinutesTo5(Math.max(0, minutes))
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`
  return `${m}min`
}

/**
 * Duração entre dois horários "HH:MM" do mesmo dia (23:55 → 00:05 soma 24h).
 * Sem os dois horários, cai no fallback da rota.
 *
 * A busca formatava isso por conta própria com `${h}h ${m}min`, então um
 * trecho de 2h saía "2h 0min" enquanto o próprio fallback da mesma tela dizia
 * "2h". Unifica no formato de `formatDurationMinutes`/`ceilDurationTo5`.
 */
export function durationBetweenTimes(
  from: string | null | undefined,
  to: string | null | undefined,
  fallbackDuration: string | null | undefined
): string {
  if (!from || !to) return ceilDurationTo5(fallbackDuration)
  const [ah, am] = from.split(":").map(Number)
  const [bh, bm] = to.split(":").map(Number)
  if (![ah, am, bh, bm].every(Number.isFinite)) return ceilDurationTo5(fallbackDuration)
  let minutes = bh * 60 + bm - (ah * 60 + am)
  if (minutes < 0) minutes += 24 * 60
  return formatDurationMinutes(minutes)
}

/**
 * Chave de coordenada da parada, para bater parada ↔ ponto da rota sem depender
 * do nome (dois municípios podem se chamar igual) nem da posição (ponto sem
 * coordenada é pulado por `getRoutePoints` e desalinizaria os ordinais).
 */
function stopKey(point: { lat: number; lng: number } | null): string | null {
  return point ? `${point.lat},${point.lng}` : null
}

/** "Santa Terezinha de Itaipu, Paraná, ..." → "Santa Terezinha de Itaipu". */
export function shortPlaceName(name: string): string {
  return splitPlaceName(name).primary || name
}

/** Horário em `km` da rota, pela mesma fração usada em `computePointTimes`. */
function timeAtKm(
  ride: unknown,
  km: number,
  totalKm: number,
  totalMinutes: number | null
): string {
  const start = rideStartDate(ride)
  if (!start || !totalMinutes || totalKm <= 0) return ""
  return formatTime(new Date(start.getTime() + (km / totalKm) * totalMinutes * 60000))
}

interface RawStop extends RideLocationLike {
  cityName?: string | null
}

interface ReservationLocation {
  name: string
  key: string
  point?: RoutePoint
  /** Km na rota quando a ponta não coincide com uma parada (linha sintética). */
  alongKm?: number
  time?: string
  boarders: Array<{ name: string; avatar: string | null }>
  alighters: Array<{ name: string; avatar: string | null }>
}

/**
 * Ponta (embarque/desembarque) de uma reserva resolvida para um ponto da rota
 * ou para um km sintético. `point` vence quando o km/índice casa uma parada.
 */
type ResolvedReservationEnd =
  | { kind: "point"; point: RoutePoint; time: string }
  | { kind: "km"; km: number; time: string }

function findRoutePointForLocation(
  locationName: string,
  points: RoutePoint[],
  timeByIndex: Map<number, string>,
  cityNameByKey: Map<string, string>
): { point: RoutePoint; time: string } | null {
  const searchName = shortPlaceName(locationName)
  for (const point of points) {
    const key = stopKey(point)
    const cityName = key ? cityNameByKey.get(key) : undefined
    // Cidade da parada quando existe (ex.: "Cascavel" numa parada "Havan"),
    // senão o próprio nome.
    const { primary } = splitPlaceName(point.name, cityName)
    if (primary === searchName) {
      return { point, time: timeByIndex.get(point.index) ?? "" }
    }
  }
  return null
}

function resolveReservationEnd(
  end: { km?: number | null; stopIndex?: number | null },
  locationName: string,
  points: RoutePoint[],
  alongKm: number[],
  timeByIndex: Map<number, string>,
  cityNameByKey: Map<string, string>,
  timeForKm: (km: number) => string
): ResolvedReservationEnd | null {
  const km = typeof end.km === "number" && Number.isFinite(end.km) ? end.km : null
  if (km != null) {
    const idx = points.findIndex((_, i) => Math.abs((alongKm[i] ?? 0) - km) <= COINCIDENT_EPSILON_KM)
    if (idx >= 0) {
      return { kind: "point", point: points[idx], time: timeByIndex.get(points[idx].index) ?? "" }
    }
    return { kind: "km", km, time: timeForKm(km) }
  }

  const stopIndex = end.stopIndex
  if (
    typeof stopIndex === "number" &&
    Number.isInteger(stopIndex) &&
    stopIndex >= 0 &&
    stopIndex < points.length
  ) {
    const point = points[stopIndex]
    return { kind: "point", point, time: timeByIndex.get(point.index) ?? "" }
  }

  const match = findRoutePointForLocation(locationName, points, timeByIndex, cityNameByKey)
  return match ? { kind: "point", point: match.point, time: match.time } : null
}

function collectReservationLocations(
  reservations: BuildRouteTimelineOptions["reservations"] = [],
  points: RoutePoint[],
  alongKm: number[],
  timeByIndex: Map<number, string>,
  cityNameByKey: Map<string, string>,
  timeForKm: (km: number) => string
): Map<string, ReservationLocation> {
  const locations = new Map<string, ReservationLocation>()

  const add = (
    resolved: ResolvedReservationEnd | null,
    locationName: string,
    side: "boarders" | "alighters",
    person: { name: string; avatar: string | null }
  ) => {
    if (!resolved) return
    const key =
      resolved.kind === "point"
        ? stopKey(resolved.point) ?? `name:${locationName}`
        : `km:${resolved.km.toFixed(3)}`
    const existing = locations.get(key) || { name: locationName, key, boarders: [], alighters: [] }
    if (resolved.kind === "point") {
      existing.point = resolved.point
    } else {
      existing.alongKm = resolved.km
    }
    existing.time = resolved.time
    existing[side].push(person)
    locations.set(key, existing)
  }

  for (const res of reservations) {
    const person = { name: res.name, avatar: res.avatar }
    if (res.origin) {
      add(
        resolveReservationEnd(
          { km: res.boardKm, stopIndex: res.boardStopIndex },
          res.origin,
          points,
          alongKm,
          timeByIndex,
          cityNameByKey,
          timeForKm
        ),
        res.origin,
        "boarders",
        person
      )
    }
    if (res.destination) {
      add(
        resolveReservationEnd(
          { km: res.alightKm, stopIndex: res.alightStopIndex },
          res.destination,
          points,
          alongKm,
          timeByIndex,
          cityNameByKey,
          timeForKm
        ),
        res.destination,
        "alighters",
        person
      )
    }
  }

  return locations
}

export function buildRouteTimeline(
  ride: unknown,
  opts: BuildRouteTimelineOptions = {}
): RouteTimelineData {
  const {
    boardIndex = 0,
    alightIndex = Number.MAX_SAFE_INTEGER,
    continuousMode = false,
    boardKm,
    alightKm,
    boardName,
    alightName,
    boardCityName,
    alightCityName,
    boardTime,
    alightTime,
    collapseIntermediate = false,
    reservations = [],
  } = opts

  const empty: RouteTimelineData = { points: [], durationMinutes: null, trechoDurationMinutes: null }

  const points: RoutePoint[] = getRoutePoints(ride)
  if (points.length < 2) return empty

  const alongKm = cumulativeKmAtRoutePoints(ride, points)
  const totalKm = alongKm.length > 0 ? alongKm[alongKm.length - 1] : 0

  const rawStops: RawStop[] = Array.isArray(
    (ride as { stops?: RawStop[] } | null)?.stops
  )
    ? (ride as { stops: RawStop[] }).stops
    : []
  const cityNameByKey = new Map<string, string>()
  for (const stop of rawStops) {
    if (!stop?.cityName) continue
    const key = stopKey(locationPoint(stop))
    if (key) cityNameByKey.set(key, stop.cityName)
  }

  const timeByIndex = new Map<number, string>()
  const times = computePointTimes(ride)
  for (const entry of times) timeByIndex.set(entry.index, entry.date ? formatTime(entry.date) : "")

  const dated = times.filter((t) => t.date)
  const first = dated[0]
  const last = dated[dated.length - 1]
  const spanMinutes =
    first?.date && last?.date
      ? Math.round((last.date.getTime() - first.date.getTime()) / 60000)
      : 0
  const durationMinutes = spanMinutes > 0 ? spanMinutes : null

  // Limites do trecho: em km no modo contínuo, no km da parada nos outros modos.
  const boardAtKm = continuousMode ? boardKm : alongKm[boardIndex]
  const alightAtKm = continuousMode ? alightKm : alongKm[alightIndex]
  const hasTrecho =
    Number.isFinite(boardAtKm) &&
    Number.isFinite(alightAtKm) &&
    (alightAtKm as number) >= (boardAtKm as number)
  const trechoStartKm = hasTrecho ? (boardAtKm as number) : 0
  const trechoEndKm = hasTrecho ? (alightAtKm as number) : totalKm

  const groupFor = (km: number): RouteTimelineGroup => {
    if (km < trechoStartKm - COINCIDENT_EPSILON_KM) return "before"
    if (km > trechoEndKm + COINCIDENT_EPSILON_KM) return "after"
    return "in"
  }

  // Collect reservation boarding/alighting locations
  const reservationLocations = collectReservationLocations(
    reservations,
    points,
    alongKm,
    timeByIndex,
    cityNameByKey,
    (km) => timeAtKm(ride, km, totalKm, durationMinutes)
  )

  // Build base points from route
  const built: RouteTimelinePoint[] = points.map((point, i) => {
    const km = alongKm[i] ?? 0
    const key = stopKey(point)
    const cityName = key ? cityNameByKey.get(key) : undefined
    const { primary, secondary } = splitPlaceName(point.name, cityName)
    
    // Add reservation passengers for this point
    const loc = key ? reservationLocations.get(key) : undefined
    return {
      index: point.index,
      name: primary,
      secondary,
      time: timeByIndex.get(point.index) ?? "",
      kind: point.kind,
      group: groupFor(km),
      alongKm: km,
      boarders: loc?.boarders,
      alighters: loc?.alighters,
    }
  })

  // Add synthetic points for reservation locations that don't match route points
  for (const [key, loc] of reservationLocations) {
    if (loc.point) continue // Already matched to a route point
    if (loc.boarders.length === 0 && loc.alighters.length === 0) continue

    const { primary, secondary } = splitPlaceName(loc.name)
    // O mesmo km pode ser embarque de um passageiro e desembarque de outro
    // (ex.: Medianeira no meio do trecho): embarque tem prioridade na linha.
    const isBoard = loc.boarders.length > 0
    const km = typeof loc.alongKm === "number" ? loc.alongKm : Number.NaN
    built.push({
      index: syntheticIndex(key, isBoard),
      name: primary,
      secondary,
      time: loc.time || "",
      kind: isBoard ? "board" : "alight",
      group: Number.isFinite(km) ? groupFor(km) : "in",
      alongKm: km, // NaN quando o ponto não tem posição conhecida na rota
      synthetic: true,
      boarders: loc.boarders,
      alighters: loc.alighters,
    })
  }

  // Linhas sintéticas só fazem sentido quando o trecho é parcial e não coincide
  // com uma parada existente; coincidir com a rota seria duplicar a linha.
  const hasPointAt = (km: number) =>
    built.some((p) => Math.abs(p.alongKm - km) <= COINCIDENT_EPSILON_KM)

  let boardPoint: RouteTimelinePoint | null = null
  let alightPoint: RouteTimelinePoint | null = null
  if (hasTrecho && trechoEndKm - trechoStartKm > COINCIDENT_EPSILON_KM) {
    if (!hasPointAt(trechoStartKm)) {
      const boardParts = boardName?.trim() ? splitPlaceName(boardName, boardCityName) : null
      boardPoint = {
        index: SYNTHETIC_BOARD_INDEX,
        name: boardParts?.primary || "Embarque",
        secondary: boardParts?.secondary ?? null,
        time: boardTime || timeAtKm(ride, trechoStartKm, totalKm, durationMinutes),
        kind: "board",
        group: "in",
        alongKm: trechoStartKm,
        synthetic: true,
      }
    }
    if (!hasPointAt(trechoEndKm)) {
      const alightParts = alightName?.trim() ? splitPlaceName(alightName, alightCityName) : null
      alightPoint = {
        index: SYNTHETIC_ALIGHT_INDEX,
        name: alightParts?.primary || "Desembarque",
        secondary: alightParts?.secondary ?? null,
        time: alightTime || timeAtKm(ride, trechoEndKm, totalKm, durationMinutes),
        kind: "alight",
        group: "in",
        alongKm: trechoEndKm,
        synthetic: true,
      }
    }
  }

  // Quando o trecho cobre a rota inteira (nada cai fora: sem before/after), as
  // paradas de passagem do meio não são o "trajeto do passageiro" — nenhuma é
  // ponto de embarque/desembarque do usuário. O mesmo vale para a rota vista
  // sem trecho (página aberta direto): tudo que está estritamente entre origem
  // e destino fica atrás de um "… ver N paradas", inclusive paradas com
  // marcadores de passageiro e linhas sintéticas de reservas — os marcadores
  // voltam a aparecer quando o grupo é aberto. Os endpoints nunca colapsam:
  // origem/destino da rota e qualquer ponto no km do embarque/desembarque
  // (inclusive as linhas sintéticas do próprio trecho consultado).
  let hasMiddle = false

  // Check if trecho covers full route (origin to destination)
  const firstRouteKm = alongKm[0] ?? 0
  const lastRouteKm = alongKm[alongKm.length - 1] ?? 0
  const trechoCoversFullRoute =
    Number.isFinite(boardAtKm) &&
    Number.isFinite(alightAtKm) &&
    boardAtKm <= firstRouteKm + COINCIDENT_EPSILON_KM &&
    alightAtKm >= lastRouteKm - COINCIDENT_EPSILON_KM

  // Collapse middle points when: (1) all points are "in" (rota sem trecho ou
  // trecho da origem ao destino), OR (2) trecho covers full route.
  if (built.every((p) => p.group === "in") || trechoCoversFullRoute) {
    for (const p of built) {
      if (p.kind === "origin" || p.kind === "destination") continue
      if (Number.isFinite(p.alongKm)) {
        if (p.alongKm <= firstRouteKm + COINCIDENT_EPSILON_KM) continue
        if (p.alongKm >= lastRouteKm - COINCIDENT_EPSILON_KM) continue
      }
      hasMiddle = true
      p.group = "middle"
    }
  }

  // Vindo da busca (`collapseIntermediate`) o trilho é o trecho: tudo que está
  // estritamente entre o embarque e o desembarque — paradas reais de passagem e
  // linhas sintéticas de outros passageiros — fica atrás do "… ver N paradas".
  // Os endpoints (km igual a trechoStartKm/trechoEndKm) nunca entram: são o
  // próprio embarque/desembarque do trecho consultado.
  if (collapseIntermediate && hasTrecho) {
    for (const p of built) {
      if (!Number.isFinite(p.alongKm)) continue
      if (p.alongKm <= trechoStartKm + COINCIDENT_EPSILON_KM) continue
      if (p.alongKm >= trechoEndKm - COINCIDENT_EPSILON_KM) continue
      hasMiddle = true
      p.group = "middle"
    }
  }

  // Ordena por km na rota. Pontos sem km conhecido (linha sintética de reserva
  // sem km/índice/coincidência de nome) vão para o fim, como antes.
  const sortByKm = (list: RouteTimelinePoint[]) =>
    [...list].sort((a, b) => {
      const aKnown = Number.isFinite(a.alongKm)
      const bKnown = Number.isFinite(b.alongKm)
      if (aKnown && bKnown) return a.alongKm - b.alongKm
      if (aKnown) return -1
      if (bKnown) return 1
      return 0
    })

  const allPoints = sortByKm(built)

  // Com `middle` a rota já está na ordem certa (origem → paradas → destino) e
  // não há linha sintética: os limites coincidem com a origem e o destino, e um
  // ponto a ≤ 50 m deles é o próprio ponto. As linhas de embarque/desembarque
  // do trecho (quando existem) têm que entrar aqui — senão somem da tela. Nos
  // outros casos, monta na ordem before → embarque → trecho → desembarque → after.
  const ordered: RouteTimelinePoint[] = hasMiddle
    ? sortByKm([
        ...built,
        ...(boardPoint ? [boardPoint] : []),
        ...(alightPoint ? [alightPoint] : []),
      ])
    : [
        ...allPoints.filter((p) => p.group === "before"),
        ...(boardPoint ? [boardPoint] : []),
        ...allPoints.filter((p) => p.group === "in"),
        ...(alightPoint ? [alightPoint] : []),
        ...allPoints.filter((p) => p.group === "after"),
      ]

  const trechoDurationMinutes =
    durationMinutes == null || totalKm <= 0
      ? null
      : hasTrecho
        ? Math.max(
            0,
            Math.round((durationMinutes * (trechoEndKm - trechoStartKm)) / totalKm)
          )
        : durationMinutes

  return { points: ordered, durationMinutes, trechoDurationMinutes }
}
